import { Keyboard, X } from 'lucide-react';
import React, { useState, useEffect } from 'react';

import {
  PADDING_X,
  PADDING_Y,
  GAP_SCALE,
  SIZE_SCALE,
  TYPE_SCALE,
  SURFACE_PADDING,
  SPACE_Y_SCALE,
  RADIUS_SCALE,
  SHADOW_SCALE,
  COLOR_SCALE,
} from '@/components/design-system/tokens';
import { withMemoryLeakMonitor } from '@/components/ui/MemoryLeakMonitor';
import { useTranslation } from '@/hooks/useTranslation';
import { BREAKPOINTS } from '@/utils/responsive';

import { Kbd, KbdGroup } from './kbd';

/**
 * KeyboardShortcutsHint Component
 *
 * Shows a helpful hint about available keyboard shortcuts
 * - Only appears when user presses Shift + ?
 * - Can be dismissed by clicking X or pressing Shift + ? again
 * - Only shown on desktop devices (1024px and above)
 */
const KeyboardShortcutsHint: React.FC = () => {
  const { t } = useTranslation();
  const typedT = t as (page: string, context: string, fallback?: string) => string;
  const [isVisible, setIsVisible] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  // Check if device is desktop (has keyboard)
  useEffect(() => {
    const checkIfDesktop = () => {
      // Only show on desktop screens (1024px and above)
      setIsDesktop(window.innerWidth >= BREAKPOINTS.lg);
    };

    checkIfDesktop();
    window.addEventListener('resize', checkIfDesktop);

    return () => window.removeEventListener('resize', checkIfDesktop);
  }, []);

  // No auto-show behavior - only show when user presses Shift + ?

  // Simple dismiss handler
  const handleDismiss = () => {
    setIsVisible(false);
  };

  // Show hint on Shift + '?' keypress (desktop only)
  useEffect(() => {
    if (!isDesktop) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      // Check for Shift + ? (which is typically Shift + /)
      if (e.key === '?' && e.shiftKey) {
        setIsVisible(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isDesktop]);

  // Don't render at all on mobile/tablet
  if (!isDesktop) return null;

  return (
    <>
      {/* Hint Badge - Fixed bottom right */}
      <div
        className={`fixed bottom-6 right-6 z-[9999] transition-all duration-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <div
          className={`bg-gradient-to-br from-slate-800 to-slate-900 text-white ${RADIUS_SCALE.xl} ${SHADOW_SCALE['2xl']} border ${COLOR_SCALE['shortcuts-border']} overflow-hidden max-w-sm`}
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between ${PADDING_X['4']} ${PADDING_Y['3']} bg-slate-900/50 border-b ${COLOR_SCALE['shortcuts-border']}`}
          >
            <div className={`flex items-center ${GAP_SCALE['2']}`}>
              <Keyboard
                className={`${SIZE_SCALE['4']} ${SIZE_SCALE['4']} ${COLOR_SCALE['shortcuts-icon']}`}
              />
              <strong className={`${TYPE_SCALE.sm} font-semibold text-white`}>
                {typedT('keyboard_shortcuts', 'title', 'Keyboard Shortcuts')}
              </strong>
            </div>
            <button
              onClick={handleDismiss}
              className={`${SURFACE_PADDING['1']} hover:bg-slate-700 ${RADIUS_SCALE.md} transition-colors`}
              aria-label={typedT('keyboard_shortcuts', 'close_hint', 'Close')}
            >
              <X className={`${SIZE_SCALE['4']} ${SIZE_SCALE['4']}`} />
            </button>
          </div>

          {/* Shortcuts List */}
          <div className={`${SURFACE_PADDING['4']} ${SPACE_Y_SCALE['3']}`}>
            <div className={`flex items-center justify-between ${GAP_SCALE['3']} ${TYPE_SCALE.sm}`}>
              <span className={`${COLOR_SCALE['slate-300']} flex-1`}>
                {typedT('keyboard_shortcuts', 'scroll_down', 'Scroll down')}
              </span>
              <Kbd className={COLOR_SCALE['slate-200']}>
                {typedT('common', 'keyboard_shortcuts.key_space', 'Space')}
              </Kbd>
            </div>

            <div className={`flex items-center justify-between ${GAP_SCALE['3']} ${TYPE_SCALE.sm}`}>
              <span className={`${COLOR_SCALE['slate-300']} flex-1`}>
                {typedT('keyboard_shortcuts', 'scroll_up', 'Back to top')}
              </span>
              <div className={`flex items-center ${GAP_SCALE['1']} flex-shrink-0`}>
                <KbdGroup>
                  <Kbd className={COLOR_SCALE['slate-200']}>
                    {typedT('common', 'keyboard_shortcuts.key_shift', 'Shift')}
                  </Kbd>
                  <span className={`${COLOR_SCALE['slate-500']} ${TYPE_SCALE.xs}`}>+</span>
                  <Kbd className={COLOR_SCALE['slate-200']}>
                    {typedT('common', 'keyboard_shortcuts.key_up', '↑')}
                  </Kbd>
                </KbdGroup>
              </div>
            </div>

            <div className={`flex items-center justify-between ${GAP_SCALE['3']} ${TYPE_SCALE.sm}`}>
              <span className={`${COLOR_SCALE['slate-300']} flex-1`}>
                {typedT('keyboard_shortcuts', 'show_help', 'Toggle this help')}
              </span>
              <div className={`flex items-center ${GAP_SCALE['1']} flex-shrink-0`}>
                <KbdGroup>
                  <Kbd className={COLOR_SCALE['slate-200']}>
                    {typedT('common', 'keyboard_shortcuts.key_shift', 'Shift')}
                  </Kbd>
                  <span className={`${COLOR_SCALE['slate-500']} ${TYPE_SCALE.xs}`}>+</span>
                  <Kbd className={COLOR_SCALE['slate-200']}>?</Kbd>
                </KbdGroup>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const KeyboardShortcutsHintWithMonitor = withMemoryLeakMonitor(KeyboardShortcutsHint, {
  componentName: 'KeyboardShortcutsHint',
  enableRenderTracking: true,
  enableLeakDetection: true,
  warnOnExcessiveRenders: false, // Only renders when user interacts
});

export default KeyboardShortcutsHintWithMonitor;
